/**
 * Servicio de envío de recetas de usuarios
 *
 * IMPORTANTE: el backend de recetas aún NO existe. Este fichero es el ÚNICO
 * punto de acoplamiento entre el formulario de envío de recetas y el
 * proveedor real de almacenamiento/envío. Si en el futuro se cambia de
 * proveedor (otro endpoint, un servicio de terceros, etc.), basta con
 * modificar la función `submitRecipe` de este fichero; el resto de la
 * aplicación no debería enterarse del cambio.
 */

/** Endpoint al que se envía el formulario (configurable por variable de entorno). */
const ENDPOINT = process.env.REACT_APP_RECIPE_ENDPOINT || '/api/recetas/';

/** Número máximo de imágenes permitidas por envío. */
export const MAX_IMAGENES = 3;

/**
 * Número mínimo de imágenes exigido por envío.
 *
 * El formulario requiere al menos una foto de la receta: una receta sin
 * imagen tiene mucho menos valor para publicar en la web y redes sociales,
 * así que se exige como campo obligatorio igual que el nombre o el email.
 */
export const MIN_IMAGENES = 1;

/** Tamaño máximo permitido por imagen, en bytes (5 MB). */
export const MAX_TAMANO_IMAGEN_BYTES = 5 * 1024 * 1024;

/** Tipos MIME de imagen aceptados. */
export const TIPOS_IMAGEN_PERMITIDOS = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Envía la receta de un usuario al backend (cuando exista) mediante FormData,
 * ya que el envío incluye ficheros de imagen además de campos de texto.
 *
 * @param {Object} payload - Datos del formulario de envío de receta.
 * @param {string} payload.nombre - Nombre completo de quien envía la receta.
 * @param {string} payload.email - Correo electrónico de contacto.
 * @param {string} payload.nombre_receta - Nombre de la receta.
 * @param {string} payload.receta - Texto de la receta (ingredientes y elaboración).
 * @param {File[]} payload.imagenes - Imágenes adjuntas de la receta (obligatorio,
 *   mínimo {@link MIN_IMAGENES}, máximo {@link MAX_IMAGENES}).
 * @param {boolean} payload.consentimientoPublicacion - Consentimiento explícito
 *   para publicar la receta y las imágenes en la web y redes sociales.
 * @param {boolean} payload.aceptaPrivacidad - Aceptación de la política de
 *   privacidad y el tratamiento de los datos para gestionar el envío.
 * @returns {Promise<Object>} Respuesta JSON del backend, o `{ ok: true }` si
 *   la respuesta no trae cuerpo JSON.
 * @throws {Error} Si la respuesta HTTP no es correcta (`!response.ok`), con
 *   un mensaje en español apto para mostrar al usuario.
 */
export const submitRecipe = async (payload) => {
  const {
    nombre,
    email,
    nombre_receta: nombreReceta,
    receta,
    imagenes = [],
    consentimientoPublicacion,
    aceptaPrivacidad,
  } = payload;

  // Construimos FormData porque el envío combina campos de texto con
  // ficheros binarios (imágenes), algo que un JSON plano no puede transportar.
  const formData = new FormData();
  formData.append('nombre', nombre);
  formData.append('email', email);
  formData.append('nombre_receta', nombreReceta);
  formData.append('receta', receta);
  formData.append('consentimiento_publicacion', consentimientoPublicacion);
  formData.append('acepta_privacidad', aceptaPrivacidad);

  // Las imágenes se añaden repitiendo la misma clave 'imagenes[]' (CON
  // corchetes): es la única forma en que PHP agrupa varias subidas bajo la
  // misma clave en $_FILES. Sin los corchetes, PHP solo vería la ÚLTIMA
  // imagen del envío (las demás se pisarían en silencio); ver
  // `frontend/public/api/src/Http/FilesNormalizer.php`.
  imagenes.forEach((imagen) => {
    formData.append('imagenes[]', imagen);
  });

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    // El backend valida de nuevo todo lo que valida el formulario, y puede
    // rechazar algo que aquí pasó (por ejemplo, un nombre demasiado corto).
    // Si devuelve errores por campo hay que propagarlos para pintarlos en su
    // sitio: enseñar un "inténtalo más tarde" genérico ante un error de
    // validación hace creer al usuario que el servicio está caído y le deja
    // sin saber qué corregir.
    let datos = null;
    try {
      datos = await response.json();
    } catch (error) {
      datos = null;
    }

    if (response.status === 429) {
      throw new Error(
        'Has enviado varias recetas seguidas. Espera un rato antes de mandar otra.'
      );
    }

    if (response.status === 400 && datos && typeof datos === 'object') {
      const error = new Error(
        'Revisa los campos marcados: el servidor no ha aceptado el envío.'
      );
      // Errores por campo, con la nomenclatura del backend (snake_case).
      error.camposConError = datos;
      throw error;
    }

    throw new Error(
      'No se ha podido enviar la receta. Inténtalo de nuevo en unos minutos.'
    );
  }

  // Si el backend no devuelve cuerpo (p. ej. 204 No Content), devolvemos un
  // objeto de éxito genérico en lugar de fallar al parsear JSON vacío.
  try {
    return await response.json();
  } catch (error) {
    return { ok: true };
  }
};
